import { isKnownBackendErrorKey } from '@/lib/api/exception.ts';
import { ApiErrorTranslationKeys } from '@/lib/api/apiErrorTranslationKeys.ts';

/**
 * Backend error keys for the public booking flow. Detect these with
 * `isKnownBackendErrorKey` to show tailored handling to the user.
 *
 * Values are `TranslationKey` enum member names — see `hasErrorKey` for why the
 * dotted variant (`error.booking.price_changed`) never matches.
 */
export const BookingErrorKeys = {
  /** 409: expectedTotals sent with book diverged from the server-side pricing. */
  PRICE_CHANGED: 'BOOKING_PRICE_CHANGED',
  /** 400: duplicated participant temporalId within the draft. */
  DUPLICATE_PARTICIPANT_ID: 'BOOKING_DUPLICATE_PARTICIPANT_ID',
  /** 404: unknown booking. */
  NOT_FOUND: 'BOOKING_NOT_FOUND',
} as const satisfies Record<string, keyof typeof ApiErrorTranslationKeys>;

export const isBookingPriceChangedError = (error: Error): boolean =>
  isKnownBackendErrorKey(error, BookingErrorKeys.PRICE_CHANGED);

/**
 * Powody, dla których wyjazd nie przyjmuje rezerwacji. Backend rozbił dawny
 * wspólny `TOUR_BOOKING_NOT_AVAILABLE` na osobne kody — okno zapisów i stan
 * wyjazdu osobno, brak miejsc osobno.
 *
 * Wszystkie znaczą to samo dla frontu: dane wyjazdu, na których zbudowaliśmy
 * widok, są nieaktualne. Właściwą reakcją jest odświeżenie `basicInfo`
 * i przeładowanie route — `resolveBookingView` pokaże wtedy właściwy ekran.
 */
export const RegistrationErrorKeys = {
  /** 400: nie ustawiono daty otwarcia zapisów. */
  NOT_SCHEDULED: 'TOUR_REGISTRATION_NOT_SCHEDULED',
  /** 400: zapisy jeszcze się nie zaczęły. */
  NOT_STARTED: 'TOUR_REGISTRATION_NOT_STARTED',
  /** 400: po rzeczywistym końcu zapisów. */
  CLOSED: 'TOUR_REGISTRATION_CLOSED',
  /** 400: wyjazd odwołany. */
  TOUR_CANCELLED: 'TOUR_CANCELLED',
  /** 400: brak wolnych miejsc — od tej zmiany osobny kod od zamkniętych zapisów. */
  TOUR_FULL: 'TOUR_FULL',
} as const satisfies Record<string, keyof typeof ApiErrorTranslationKeys>;

const registrationErrorKeyValues = Object.values(RegistrationErrorKeys);

/**
 * Czy backend odmówił z powodu okna zapisów, stanu wyjazdu albo braku miejsc.
 */
export const isRegistrationUnavailableError = (error: Error): boolean =>
  registrationErrorKeyValues.some((key) => isKnownBackendErrorKey(error, key));

/** 400: zapis na listę rezerwową poza jej oknem. */
export const WaitlistErrorKeys = {
  NOT_AVAILABLE: 'WAITLIST_NOT_AVAILABLE',
} as const satisfies Record<string, keyof typeof ApiErrorTranslationKeys>;

export const isWaitlistNotAvailableError = (error: Error): boolean =>
  isKnownBackendErrorKey(error, WaitlistErrorKeys.NOT_AVAILABLE);
